import React, { useState, useRef, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import './Header.css';
import pcLogo from '../../../../assets/Images/pc-logo.png';
import { getUtmQsParams } from '@/shared/utils/utmUtils';

export interface HeaderProps {
  className?: string;
  onBuyNowClick?: () => void;
  onLogoClick?: () => void;
}

interface DropdownItem {
  label: string;
  subtitle?: string;
  url?: string;
  onClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ className = '', onBuyNowClick, onLogoClick }) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  // const navigate = useNavigate();

  const utmQsParams = getUtmQsParams(new URLSearchParams(location.search));

  // Close dropdown when clicking outside and handle escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      // Close mobile menu when screen becomes larger than 768px
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobileMenuOpen]);

  const handleDropdownToggle = (dropdownName: string) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveDropdown(null); // Close any open dropdowns when toggling mobile menu
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setActiveDropdown(null);
  };

  const handleBuyNowClick = () => {
    if (onBuyNowClick) {
      onBuyNowClick();
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false); // Close mobile menu when buy now is clicked
  };

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else {
      // Fallback to direct navigation
      window.location.href =`/${utmQsParams}`
    }
  };

  const downloadBrochureItems: DropdownItem[] = [
    {
      label: 'Travelsafe',
      onClick: () => {
        handleExternalLink('https://cdn.prod.website-files.com/685ac8e11e786ddc90278256/685ac8e11e786ddc90278495_Travelsafe%20Brochure_2024-01%20(January%2010).pdf');
      }
    },
    {
      label: 'ShortSecure',
      onClick: () => {
        handleExternalLink('https://cdn.prod.website-files.com/685ac8e11e786ddc90278256/685ac8e11e786ddc90278457_ShortSecure%20Flyer_2023-08%20(August%2025.pdf');
      }
    },
    {
      label: 'Worldwide Elite - Inbound',
      onClick: () => {
        handleExternalLink('https://cdn.prod.website-files.com/685ac8e11e786ddc90278256/685ac8e11e786ddc902784a8_WWE%20Inbound%20with%20COVID-19%20Coverage_2023-09%20(September%2015).pdf');
      }
    }
  ];

  const productsItems: DropdownItem[] = [
    {
      label: 'Worldwide Elite (Including Schengen)',
      subtitle: 'Euro & Dollar Plans for Worldwide Travel',
      url: 'https://www.pacificcross.com.ph/insurance-plan/worldwide-elite'
    },
    {
      label: 'Asia and Oceania',
      subtitle: 'Dollar & Peso Plans for Asia & Oceania',
      url: 'https://www.pacificcross.com.ph/insurance-plan/asia-and-oceania'
    },
    {
      label: 'Domestic',
      subtitle: 'Peso Plans for trips within Philippines',
      url: 'https://www.pacificcross.com.ph/insurance-plan/domestic'
    },
    {
      label: 'ShortSecure',
      subtitle: 'Travel Plans for Land and Sea Travel',
      url: 'https://www.pacificcross.com.ph/insurance-plan/shortsecure'
    },
    {
      label: 'Worldwide Elite-Inbound',
      subtitle: 'For visitors coming to the Philippines',
      url: 'https://www.pacificcross.com.ph/insurance-plan/worldwide-elite-inbound'
    }
  ];

  const renderDropdown = (items: DropdownItem[], dropdownName: string) => {
    if (activeDropdown !== dropdownName) return null;

    return (
      <div className="header-dropdown-menu">
        {items.map((item, index) => (
          <button
            key={index}
            className="header-dropdown-item"
            onClick={() => {
              if (item.url) {
                handleExternalLink(item.url);
              } else if (item.onClick) {
                item.onClick();
              }
            }}
          >
            <div className="header-dropdown-item-content">
              <div className="header-dropdown-item-label">{item.label}</div>
              {item.subtitle && (
                <div className="header-dropdown-item-subtitle">{item.subtitle}</div>
              )}
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <header className={`header ${className}`} ref={headerRef}>
      <div className="header-container">
        <div className="header-content">
          {/* Logo Section */}
          <div className="header-logo-section">
            <img
              src={pcLogo}
              alt="Pacific Cross Logo"
              className="header-logo"
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
            />
          </div>

          {/* Desktop Navigation Section */}
          <nav className="header-navigation header-desktop-nav">
            {/* Check Website */}
            <button
              className="header-nav-item header-nav-link"
              onClick={() => handleExternalLink('https://www.pacificcross.com.ph/')}
            >
              CHECK WEBSITE
            </button>

            {/* Download Brochure */}
            <div className="header-nav-item header-dropdown">
              <button
                className="header-nav-link header-dropdown-trigger"
                onClick={() => handleDropdownToggle('brochure')}
                aria-expanded={activeDropdown === 'brochure'}
                aria-haspopup="true"
              >
                DOWNLOAD BROCHURE
                <svg
                  className={`header-dropdown-arrow ${activeDropdown === 'brochure' ? 'rotated' : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                >
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </button>
              {renderDropdown(downloadBrochureItems, 'brochure')}
            </div>

            {/* Products */}
            <div className="header-nav-item header-dropdown">
              <button
                className="header-nav-link header-dropdown-trigger"
                onClick={() => handleDropdownToggle('products')}
                aria-expanded={activeDropdown === 'products'}
                aria-haspopup="true"
              >
                PRODUCTS
                <svg
                  className={`header-dropdown-arrow ${activeDropdown === 'products' ? 'rotated' : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                >
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </button>
              {renderDropdown(productsItems, 'products')}
            </div>

            {/* Buy Now */}
            <button
              className="header-nav-item header-buy-now-btn"
              onClick={handleBuyNowClick}
            >
              BUY NOW
            </button>
          </nav>

          {/* Mobile Hamburger Menu Button */}
          <button
            className="header-mobile-menu-btn"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <div className={`header-hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
              <span className="hamburger-line top-line"></span>
              <span className="hamburger-line mid-line"></span>
              <span className="hamburger-line bottom-line"></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="header-mobile-dropdown-overlay">
          <div className="header-mobile-menu">
           {/* Mobile Navigation Items */}
            <nav className="header-mobile-nav">
              {/* Check Website */}
              <button
                className="header-mobile-nav-item"
                onClick={() => {
                  handleExternalLink('https://www.pacificcross.com.ph/');
                  handleMobileMenuClose();
                }}
              >
                CHECK WEBSITE
              </button>

              {/* Download Brochure */}
              <div className="header-mobile-dropdown">
                <button
                  className="header-mobile-nav-item header-mobile-dropdown-trigger"
                  onClick={() => handleDropdownToggle('mobile-brochure')}
                  aria-expanded={activeDropdown === 'mobile-brochure'}
                  aria-haspopup="true"
                >
                  DOWNLOAD BROCHURE
                  <svg
                    className={`header-dropdown-arrow ${activeDropdown === 'mobile-brochure' ? 'rotated' : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                  >
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                </button>
                {activeDropdown === 'mobile-brochure' && (
                  <div className="header-mobile-dropdown-menu">
                    {downloadBrochureItems.map((item, index) => (
                      <button
                        key={index}
                        className="header-mobile-dropdown-item"
                        onClick={() => {
                          if (item.onClick) {
                            item.onClick();
                          }
                          handleMobileMenuClose();
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Products */}
              <div className="header-mobile-dropdown">
                <button
                  className="header-mobile-nav-item header-mobile-dropdown-trigger"
                  onClick={() => handleDropdownToggle('mobile-products')}
                  aria-expanded={activeDropdown === 'mobile-products'}
                  aria-haspopup="true"
                >
                  PRODUCTS
                  <svg
                    className={`header-dropdown-arrow ${activeDropdown === 'mobile-products' ? 'rotated' : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                  >
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                </button>
                {activeDropdown === 'mobile-products' && (
                  <div className="header-mobile-dropdown-menu">
                    {productsItems.map((item, index) => (
                      <button
                        key={index}
                        className="header-mobile-dropdown-item"
                        onClick={() => {
                          if (item.url) {
                            handleExternalLink(item.url);
                          }
                          handleMobileMenuClose();
                        }}
                      >
                        <div className="header-dropdown-item-content">
                          <div className="header-dropdown-item-label">{item.label}</div>
                          {item.subtitle && (
                            <div className="header-dropdown-item-subtitle">{item.subtitle}</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Buy Now */}
              <button
                className="header-mobile-buy-now-btn"
                onClick={() => {
                  handleBuyNowClick();
                  handleMobileMenuClose();
                }}
              >
                BUY NOW
              </button>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;